import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  CopyOutlined, CheckOutlined, MenuOutlined, CloseOutlined,
  RocketOutlined, CodeOutlined, AppstoreOutlined, BgColorsOutlined,
  KeyOutlined, ApiOutlined, ThunderboltOutlined,
  ArrowLeftOutlined, CloudUploadOutlined,
  SettingOutlined, MonitorOutlined,
  SafetyOutlined, DeploymentUnitOutlined,
} from '@ant-design/icons';

/* ───────────────────────── Sidebar sections ───────────────────────── */

const SECTIONS = [
  { id: 'intro', label: 'Introducción', icon: <DeploymentUnitOutlined /> },
  { id: 'quick-start', label: 'Inicio Rápido', icon: <RocketOutlined /> },
  { id: 'technologies', label: 'Tecnologías', icon: <AppstoreOutlined /> },
  { id: 'deployment-types', label: 'Tipos de Deploy', icon: <BgColorsOutlined /> },
  { id: 'deploy-methods', label: 'Métodos de Deploy', icon: <CloudUploadOutlined /> },
  { id: 'env-vars', label: 'Variables de Entorno', icon: <SettingOutlined /> },
  { id: 'api-keys', label: 'API Keys', icon: <KeyOutlined /> },
  { id: 'logs', label: 'Logs y Monitoreo', icon: <MonitorOutlined /> },
  { id: 'terminal', label: 'Terminal', icon: <CodeOutlined /> },
  { id: 'statuses', label: 'Estados del Servicio', icon: <ThunderboltOutlined /> },
];

/* ───────────────────────── Code block ───────────────────────── */

const CodeBlock: React.FC<{ code: string; language?: string }> = ({ code, language }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative group rounded-lg overflow-hidden my-4">
      {language && (
        <div className="bg-gray-800 dark:bg-gray-950 px-4 py-2 text-xs text-gray-400 font-mono border-b border-gray-700">
          {language}
        </div>
      )}
      <pre className="bg-gray-900 dark:bg-gray-950 text-gray-100 px-4 py-4 text-sm font-mono overflow-x-auto leading-relaxed">
        <code>{code}</code>
      </pre>
      <button
        onClick={handleCopy}
        className="absolute top-2 right-2 p-2 bg-gray-700 hover:bg-gray-600 rounded-md text-gray-300 hover:text-white transition-colors opacity-0 group-hover:opacity-100"
        title="Copiar"
      >
        {copied ? <CheckOutlined className="text-green-400" /> : <CopyOutlined />}
      </button>
    </div>
  );
};

/* ───────────────────────── Endpoint block ───────────────────────── */

const EndpointBlock: React.FC<{
  method: string;
  path: string;
  description: string;
  headers?: { key: string; value: string; note?: string }[];
  body?: string;
  response?: string;
  curlExample?: string;
}> = ({ method, path, description, headers, body, response, curlExample }) => {
  const methodColors: Record<string, string> = {
    GET: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-400',
    POST: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-400',
    PUT: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-400',
    PATCH: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-400',
    DELETE: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-400',
  };

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden my-6">
      <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <span className={`px-2.5 py-1 rounded-md text-xs font-bold font-mono ${methodColors[method] || ''}`}>
          {method}
        </span>
        <code className="text-sm font-mono text-gray-900 dark:text-white">{path}</code>
      </div>
      <div className="px-4 py-3 space-y-3">
        <p className="text-sm text-gray-700 dark:text-gray-300">{description}</p>
        {headers && headers.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Headers</p>
            <div className="space-y-1">
              {headers.map((h) => (
                <div key={h.key} className="flex items-center gap-2 text-sm">
                  <code className="bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded text-xs font-mono text-gray-800 dark:text-gray-200">{h.key}</code>
                  <span className="text-gray-500 dark:text-gray-400">→</span>
                  <span className="text-gray-700 dark:text-gray-300 text-xs">{h.value}</span>
                  {h.note && <span className="text-gray-400 text-xs">({h.note})</span>}
                </div>
              ))}
            </div>
          </div>
        )}
        {body && (
          <div>
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Body (JSON)</p>
            <CodeBlock code={body} language="json" />
          </div>
        )}
        {response && (
          <div>
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Respuesta</p>
            <CodeBlock code={response} language="json" />
          </div>
        )}
        {curlExample && (
          <div>
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Ejemplo con cURL</p>
            <CodeBlock code={curlExample} language="bash" />
          </div>
        )}
      </div>
    </div>
  );
};

/* ───────────────────────── Main component ───────────────────────── */

const DocsDeployApp: React.FC = () => {
  const [activeSection, setActiveSection] = useState('intro');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const offsets = SECTIONS.map(({ id }) => {
        const el = document.getElementById(id);
        return { id, top: el ? el.getBoundingClientRect().top : Infinity };
      });

      const current = offsets.reduce((closest, section) =>
        section.top <= 120 && section.top > closest.top ? section : closest,
        { id: offsets[0].id, top: -Infinity }
      );

      setActiveSection(current.id);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white/80 dark:bg-gray-900/80 backdrop-blur-md border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2.5 hover:opacity-80 transition-opacity">
              <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
                <img src="/favicon-logo.png" alt="Kubiy" className="w-5 h-5" />
              </div>
              <span className="font-semibold text-gray-900 dark:text-white">Kubiy Cloud</span>
            </Link>
            <div className="h-5 w-px bg-gray-300 dark:bg-gray-700"></div>
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Deploy Apps</span>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="hidden sm:flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              <ArrowLeftOutlined />
              Volver al inicio
            </Link>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
            >
              {mobileMenuOpen ? <CloseOutlined /> : <MenuOutlined />}
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex gap-8">
          {/* Sidebar */}
          <aside className={`
            ${mobileMenuOpen ? 'fixed inset-0 top-16 z-20 bg-white dark:bg-gray-900 p-6' : 'hidden'}
            lg:block lg:w-64 lg:flex-shrink-0 lg:sticky lg:top-16 lg:h-[calc(100vh-4rem)] lg:overflow-y-auto lg:py-8
          `}>
            <nav className="space-y-1">
              {SECTIONS.map((section) => (
                <button
                  key={section.id}
                  onClick={() => scrollTo(section.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-left ${
                    activeSection === section.id
                      ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  <span className="text-base">{section.icon}</span>
                  {section.label}
                </button>
              ))}
            </nav>
          </aside>

          {/* Main Content */}
          <main className="flex-1 min-w-0 py-8 lg:py-12">
            <div className="max-w-3xl">

              {/* ═══════════════ INTRODUCCIÓN ═══════════════ */}
              <section id="intro" className="mb-16 scroll-mt-24">
                <div className="flex items-center gap-3 mb-2">
                  <span className="px-3 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 rounded-full text-xs font-semibold">
                    Documentación
                  </span>
                </div>
                <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
                  Deploy Apps
                </h1>
                <p className="text-lg text-gray-600 dark:text-gray-400 leading-relaxed mb-6">
                  Kubiy Deploy Apps te permite desplegar aplicaciones web estáticas y con backend en la nube en minutos.
                  Conecta tu repositorio de GitHub para deploys automáticos, sube un archivo ZIP, o usa la API para
                  integraciones personalizadas.
                </p>
                <div className="grid sm:grid-cols-3 gap-4">
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                    <CloudUploadOutlined className="text-xl text-indigo-500 mb-2" />
                    <h3 className="font-semibold text-sm mb-1">Deploy Flexible</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">GitHub, ZIP o API — elige cómo desplegar tu app</p>
                  </div>
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                    <AppstoreOutlined className="text-xl text-indigo-500 mb-2" />
                    <h3 className="font-semibold text-sm mb-1">Multi-Tecnología</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Node.js, Python, .NET Core, React, Vue, Angular y más</p>
                  </div>
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                    <SafetyOutlined className="text-xl text-indigo-500 mb-2" />
                    <h3 className="font-semibold text-sm mb-1">Seguro por Defecto</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">SSL automático, variables de entorno cifradas y API keys</p>
                  </div>
                </div>
              </section>

              {/* ═══════════════ INICIO RÁPIDO ═══════════════ */}
              <section id="quick-start" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Inicio Rápido</h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Despliega tu primera aplicación en 4 sencillos pasos:
                </p>

                <div className="space-y-6">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 rounded-full flex items-center justify-center text-sm font-bold">1</div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Crea tu servicio</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Desde el dashboard, ve a <strong>Deploy Apps → Crear</strong>. Selecciona la tecnología de tu proyecto
                        (Node.js, React, Python, etc.) y configura el nombre, subdominio y tipo de deployment.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 rounded-full flex items-center justify-center text-sm font-bold">2</div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Elige un plan</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Selecciona el plan que mejor se adapte a tu proyecto. Para apps estáticas los planes son más simples;
                        para apps con backend puedes elegir el modo de red (compartida o dedicada) y cómputo.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 rounded-full flex items-center justify-center text-sm font-bold">3</div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Configura tu app</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                        Define las variables de entorno, el runtime (para backend), el modo de inicio y el archivo objetivo.
                        Tu servicio se provisionará automáticamente después del pago.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 rounded-full flex items-center justify-center text-sm font-bold">4</div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Despliega tu código</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Conecta tu repositorio de GitHub para auto-deploy, sube un archivo ZIP desde el dashboard,
                        o usa la API para despliegues programáticos. Tu app estará disponible en
                        <code className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-xs font-mono ml-1">https://slug.subdominio.apps1.kubiy.site</code>
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* ═══════════════ TECNOLOGÍAS ═══════════════ */}
              <section id="technologies" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Tecnologías Soportadas</h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Kubiy Deploy Apps soporta dos grandes categorías de aplicaciones: estáticas y con backend (server).
                </p>

                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Apps Estáticas</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Aplicaciones frontend que se compilan a archivos HTML, CSS y JavaScript estáticos. Se sirven desde CDN
                  y no requieren un servidor en ejecución.
                </p>
                <div className="grid sm:grid-cols-2 gap-3 mb-8">
                  {[
                    { name: 'HTML + JavaScript', desc: 'Landing pages, portfolios y sitios estáticos' },
                    { name: 'React', desc: 'SPAs con arquitectura basada en componentes' },
                    { name: 'Vue.js', desc: 'Framework progresivo para interfaces de usuario' },
                    { name: 'Angular', desc: 'Framework completo para apps empresariales' },
                    { name: 'Vite', desc: 'Build tool ultrarrápido para desarrollo moderno' },
                    { name: 'Next.js (SSG)', desc: 'Generación de sitios estáticos con React' },
                    { name: 'Nuxt.js (SSG)', desc: 'Generación de sitios estáticos con Vue' },
                  ].map((tech) => (
                    <div key={tech.name} className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-white">{tech.name}</h4>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{tech.desc}</p>
                    </div>
                  ))}
                </div>

                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Apps con Backend (Server)</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Aplicaciones que requieren un servidor en ejecución. Kubiy provisiona una instancia dedicada y
                  gestiona el proceso con PM2 (Node.js) o el runtime correspondiente.
                </p>
                <div className="grid sm:grid-cols-2 gap-3 mb-6">
                  {[
                    { name: 'Node.js', desc: 'APIs, microservicios y aplicaciones server-side JavaScript' },
                    { name: 'Python', desc: 'Django, Flask, FastAPI y aplicaciones Python' },
                    { name: '.NET Core', desc: 'APIs y aplicaciones empresariales cross-platform' },
                    { name: 'Next.js (SSR)', desc: 'Server-side rendering con React' },
                    { name: 'Nuxt.js (SSR)', desc: 'Server-side rendering con Vue' },
                  ].map((tech) => (
                    <div key={tech.name} className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-white">{tech.name}</h4>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{tech.desc}</p>
                    </div>
                  ))}
                </div>

                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Versiones de Runtime</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Para apps con backend puedes seleccionar la versión del runtime durante la configuración:
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 dark:border-gray-700">
                        <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Tecnología</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Versiones</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Recomendada</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      <tr>
                        <td className="py-3 px-4 text-gray-900 dark:text-white">Node.js</td>
                        <td className="py-3 px-4 text-gray-600 dark:text-gray-400">16, 18, 20, 22, 24</td>
                        <td className="py-3 px-4"><span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded text-xs font-medium">22 LTS</span></td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 text-gray-900 dark:text-white">Python</td>
                        <td className="py-3 px-4 text-gray-600 dark:text-gray-400">3.9, 3.10, 3.11, 3.12, 3.13, 3.14</td>
                        <td className="py-3 px-4"><span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded text-xs font-medium">3.12</span></td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 text-gray-900 dark:text-white">.NET Core</td>
                        <td className="py-3 px-4 text-gray-600 dark:text-gray-400">6, 8, 9, 10</td>
                        <td className="py-3 px-4"><span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded text-xs font-medium">10 LTS</span></td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 text-gray-900 dark:text-white">Go</td>
                        <td className="py-3 px-4 text-gray-600 dark:text-gray-400">1.21, 1.22, 1.23, 1.24, 1.25</td>
                        <td className="py-3 px-4"><span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded text-xs font-medium">1.24</span></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              {/* ═══════════════ TIPOS DE DEPLOY ═══════════════ */}
              <section id="deployment-types" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Tipos de Deployment</h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Cada servicio se crea con un tipo de deployment que define el nivel de infraestructura y aislamiento.
                </p>

                <div className="grid sm:grid-cols-2 gap-4 mb-8">
                  {[
                    { emoji: '🟣', name: 'Dev', id: 'dev', desc: 'Ideal para desarrollo y pruebas. Recursos compartidos y costo mínimo.' },
                    { emoji: '🟡', name: 'Preview', id: 'preview', desc: 'Para demos y staging. Permite validar antes de ir a producción.' },
                    { emoji: '🟢', name: 'Prod Lite', id: 'prod_lite', desc: 'Producción estándar. Buen balance entre costo y rendimiento.' },
                    { emoji: '🔵', name: 'Prod HA', id: 'prod', desc: 'Producción con alta disponibilidad. Recursos dedicados y mayor resiliencia.' },
                  ].map((dt) => (
                    <div key={dt.id} className="p-5 border border-gray-200 dark:border-gray-700 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-lg">{dt.emoji}</span>
                        <h3 className="font-semibold text-gray-900 dark:text-white">{dt.name}</h3>
                        <code className="text-xs text-gray-400 font-mono">{dt.id}</code>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{dt.desc}</p>
                    </div>
                  ))}
                </div>

                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Modos de Red y Cómputo (Solo Backend)</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Las apps con backend permiten elegir el modo de red y cómputo según los requerimientos de seguridad y rendimiento:
                </p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Red Compartida</h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Tu app comparte la infraestructura de red con otros tenants. Incluye balanceador de carga compartido,
                      certificado SSL automático y enrutamiento por subdominio.
                    </p>
                  </div>
                  <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Red Dedicada (Tenant)</h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      VPC propia para tu tenant con subnets aisladas. Mayor seguridad y control sobre la red.
                      Recomendado para producción con datos sensibles.
                    </p>
                  </div>
                  <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Cómputo Compartido</h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Tu app se ejecuta en instancias compartidas con otros servicios. Optimizado para costo.
                    </p>
                  </div>
                  <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Cómputo Dedicado</h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Instancia EC2 exclusiva para tu app. Rendimiento predecible y sin vecinos ruidosos.
                    </p>
                  </div>
                </div>
              </section>

              {/* ═══════════════ MÉTODOS DE DEPLOY ═══════════════ */}
              <section id="deploy-methods" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Métodos de Deploy</h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  El método de deploy depende del tipo de aplicación. Las apps con backend (server) usan GitHub o Kubiy CLI,
                  mientras que las apps estáticas usan la subida de archivos ZIP desde el dashboard.
                </p>

                {/* Tabla resumen */}
                <div className="overflow-x-auto mb-8">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 dark:border-gray-700">
                        <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Método</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Tipo de App</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Descripción</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      <tr>
                        <td className="py-3 px-4 font-medium text-gray-900 dark:text-white">GitHub Auto-Deploy</td>
                        <td className="py-3 px-4"><span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 rounded text-xs font-medium">Server</span></td>
                        <td className="py-3 px-4 text-gray-600 dark:text-gray-400">Deploy automático en cada push a la rama configurada</td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 font-medium text-gray-900 dark:text-white">Kubiy CLI</td>
                        <td className="py-3 px-4"><span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 rounded text-xs font-medium">Server</span></td>
                        <td className="py-3 px-4 text-gray-600 dark:text-gray-400">Deploy desde la terminal con el CLI de Kubiy</td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 font-medium text-gray-900 dark:text-white">Subida de ZIP</td>
                        <td className="py-3 px-4"><span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded text-xs font-medium">Estática</span></td>
                        <td className="py-3 px-4 text-gray-600 dark:text-gray-400">Sube un archivo .zip con los archivos compilados desde el dashboard</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* GitHub Auto-Deploy */}
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                    <CodeOutlined className="text-gray-700 dark:text-gray-300" />
                    GitHub Auto-Deploy
                  </h3>
                  <p className="text-xs text-gray-400 font-mono mb-3">Solo apps con backend (server)</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Conecta tu repositorio de GitHub y cada push a la rama configurada disparará un deploy automático.
                    Kubiy instala un webhook en tu repositorio para detectar cambios en tiempo real.
                  </p>

                  <div className="space-y-4">
                    <div className="flex gap-4">
                      <div className="flex-shrink-0 w-7 h-7 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded-full flex items-center justify-center text-xs font-bold">1</div>
                      <div className="flex-1">
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          Ve a tu app → <strong>Configuración → Conectividad</strong> y haz clic en <strong>Conectar GitHub</strong>.
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-4">
                      <div className="flex-shrink-0 w-7 h-7 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded-full flex items-center justify-center text-xs font-bold">2</div>
                      <div className="flex-1">
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          Autoriza la aplicación de Kubiy en GitHub y selecciona el repositorio.
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-4">
                      <div className="flex-shrink-0 w-7 h-7 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded-full flex items-center justify-center text-xs font-bold">3</div>
                      <div className="flex-1">
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          Elige la rama a monitorear (por defecto <code className="bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded text-xs font-mono">main</code>),
                          el modo de inicio (<code className="bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded text-xs font-mono">npm</code>, <code className="bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded text-xs font-mono">node</code>, <code className="bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded text-xs font-mono">python</code>, etc.)
                          y el archivo/script objetivo.
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-4">
                      <div className="flex-shrink-0 w-7 h-7 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded-full flex items-center justify-center text-xs font-bold">4</div>
                      <div className="flex-1">
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          Activa el auto-deploy. A partir de ahora, cada push a esa rama desplegará automáticamente.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                    <p className="text-sm text-green-800 dark:text-green-300">
                      <strong>Tip:</strong> Puedes desconectar GitHub en cualquier momento desde la configuración de la app.
                      El webhook se eliminará automáticamente de tu repositorio.
                    </p>
                  </div>
                </div>

                {/* Kubiy CLI */}
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                    <ApiOutlined className="text-gray-700 dark:text-gray-300" />
                    Kubiy CLI
                  </h3>
                  <p className="text-xs text-gray-400 font-mono mb-3">Solo apps con backend (server)</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Kubiy CLI es una herramienta de línea de comandos que te permite desplegar tu aplicación directamente
                    desde tu terminal. Ideal para integrar en pipelines de CI/CD o para deploys manuales rápidos.
                  </p>

                  <CodeBlock
                    language="bash"
                    code={`# Instalar Kubiy CLI\nnpm install -g kubiy-paas-cli\n\n# Iniciar sesión\nkubiy login\n\n# Vincular tu proyecto a la app (solo la primera vez)\nkubiy link <appId>\n\n# Desplegar\nkubiy deploy`}
                  />

                  <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                    <p className="text-sm text-blue-800 dark:text-blue-300">
                      <strong>Nota:</strong> <code>kubiy link</code> vincula tu carpeta local con una app ya creada en el dashboard — no crea una app nueva. Necesitas el <strong>appId</strong> y una <strong>API Key</strong> de tu app.
                      Consulta la sección de <button onClick={() => scrollTo('api-keys')} className="underline font-medium">API Keys</button> para generarla.
                    </p>
                  </div>
                </div>

                {/* ZIP Deploy */}
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                    <CloudUploadOutlined className="text-gray-700 dark:text-gray-300" />
                    Deploy por ZIP
                  </h3>
                  <p className="text-xs text-gray-400 font-mono mb-3">Solo apps estáticas</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Para apps estáticas, el deploy se realiza subiendo un archivo <code className="bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded text-xs font-mono">.zip</code> directamente
                    desde el dashboard. Arrastra el archivo a la zona de drop en el panel lateral o haz clic para seleccionarlo.
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    El ZIP debe contener los archivos compilados de tu app (por ejemplo, el contenido de la carpeta
                    <code className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-xs font-mono mx-1">build/</code> o
                    <code className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-xs font-mono mx-1">dist/</code>).
                    Kubiy procesará el archivo automáticamente y actualizará tu sitio.
                  </p>

                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Proceso de subida</h4>
                  <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400 mb-4">
                    <li className="flex items-center gap-2"><CheckOutlined className="text-green-500 text-xs" /> Selecciona o arrastra el archivo .zip</li>
                    <li className="flex items-center gap-2"><CheckOutlined className="text-green-500 text-xs" /> Haz clic en <strong>Deploy</strong> para iniciar la subida</li>
                    <li className="flex items-center gap-2"><CheckOutlined className="text-green-500 text-xs" /> Se genera una URL pre-firmada y el archivo se sube a S3</li>
                    <li className="flex items-center gap-2"><CheckOutlined className="text-green-500 text-xs" /> El procesamiento es automático — tu sitio se actualiza en segundos</li>
                  </ul>

                  <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                    <p className="text-sm text-amber-800 dark:text-amber-300">
                      <strong>Importante:</strong> Asegúrate de que el archivo .zip contenga los archivos en la raíz
                      (no dentro de una subcarpeta). Por ejemplo, el <code className="font-mono">index.html</code> debe
                      estar en el primer nivel del ZIP.
                    </p>
                  </div>
                </div>

                {/* Rollback */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    <ThunderboltOutlined className="text-gray-700 dark:text-gray-300" />
                    Historial y Rollback
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Cada deploy genera un registro con información del commit (para GitHub), autor, fecha y estado.
                    Desde la pestaña <strong>Deploys</strong> de tu app puedes ver el historial completo y hacer
                    rollback a cualquier versión anterior con un solo clic.
                  </p>
                  <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                    <p className="text-sm text-amber-800 dark:text-amber-300">
                      <strong>Importante:</strong> El rollback restablece el código de la versión seleccionada pero
                      no revierte las variables de entorno ni la configuración de la app.
                    </p>
                  </div>
                </div>
              </section>

              {/* ═══════════════ VARIABLES DE ENTORNO ═══════════════ */}
              <section id="env-vars" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Variables de Entorno</h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Las variables de entorno te permiten almacenar configuraciones sensibles como claves de API,
                  credenciales de base de datos y URLs de servicios externos sin exponerlas en el código.
                </p>

                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Gestión desde el Dashboard</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Ve a tu app → <strong>Configuración → Variables de Entorno</strong>. Desde ahí puedes:
                </p>
                <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400 mb-6">
                  <li className="flex items-center gap-2"><CheckOutlined className="text-green-500 text-xs" /> Agregar nuevas variables con nombre y valor</li>
                  <li className="flex items-center gap-2"><CheckOutlined className="text-green-500 text-xs" /> Marcar variables como ocultas (se enmascaran en la UI)</li>
                  <li className="flex items-center gap-2"><CheckOutlined className="text-green-500 text-xs" /> Editar o eliminar variables existentes</li>
                  <li className="flex items-center gap-2"><CheckOutlined className="text-green-500 text-xs" /> Guardar todos los cambios de una vez</li>
                </ul>

                <CodeBlock
                  language="text"
                  code={`# Ejemplo de variables de entorno\nDATABASE_URL=postgresql://user:pass@host:5432/mydb\nAPI_SECRET=sk_live_xxxxxxxxxxxx\nNODE_ENV=production\nPORT=3000`}
                />

                <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg mt-4">
                  <p className="text-sm text-amber-800 dark:text-amber-300">
                    <strong>Nota:</strong> Los cambios en variables de entorno requieren un re-deploy para que tomen efecto
                    en la aplicación en ejecución.
                  </p>
                </div>
              </section>

              {/* ═══════════════ API KEYS ═══════════════ */}
              <section id="api-keys" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">API Keys</h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Genera tokens de acceso para autenticar llamadas a tu app desplegada.
                  Cada API key tiene un nombre, un alcance y opcionalmente una fecha de expiración.
                </p>

                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Generar una API Key</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Ve a tu app → <strong>Configuración → API Keys</strong> y haz clic en <strong>Generar API Key</strong>.
                </p>
                <div className="overflow-x-auto mb-6">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 dark:border-gray-700">
                        <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Campo</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Descripción</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      <tr>
                        <td className="py-3 px-4"><code className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-xs font-mono">name</code></td>
                        <td className="py-3 px-4 text-gray-600 dark:text-gray-400">Nombre descriptivo para identificar el token (ej: "Backend Production")</td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4"><code className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-xs font-mono">expiración</code></td>
                        <td className="py-3 px-4 text-gray-600 dark:text-gray-400">Días hasta la expiración. Déjalo en "Ilimitado" si no quieres que expire.</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg mb-4">
                  <p className="text-sm text-red-700 dark:text-red-400">
                    <strong>Importante:</strong> El token completo solo se muestra una vez al generarlo. Cópialo y guárdalo
                    en un lugar seguro. Si lo pierdes, deberás generar uno nuevo (el anterior puede ser revocado).
                  </p>
                </div>

                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Revocar una API Key</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Desde la lista de API keys, haz clic en <strong>Revocar</strong> junto al token que deseas invalidar.
                  Esta acción es irreversible: cualquier sistema que use ese token dejará de tener acceso inmediatamente.
                </p>
              </section>

              {/* ═══════════════ LOGS Y MONITOREO ═══════════════ */}
              <section id="logs" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Logs y Monitoreo</h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Kubiy proporciona acceso a los logs de tu aplicación directamente desde el dashboard.
                  Disponible para apps con backend (server).
                </p>

                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Tipos de Logs</h3>
                <div className="grid sm:grid-cols-3 gap-4 mb-6">
                  <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">Deploy</h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Logs del proceso de despliegue: instalación de dependencias, build y arranque.
                    </p>
                  </div>
                  <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">App (PM2)</h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Logs de la aplicación en ejecución, incluyendo stdout y mensajes de consola.
                    </p>
                  </div>
                  <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">Errores</h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Logs de error (stderr) de la aplicación. Útil para depurar crashes y excepciones.
                    </p>
                  </div>
                </div>

                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Cómo Acceder</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Ve a tu app → pestaña <strong>Monitoreo</strong>. Selecciona el tipo de log, la cantidad de líneas
                  (50, 100, 200 o 500) y haz clic en <strong>Cargar Logs</strong>. Los logs se muestran en una terminal
                  con auto-scroll y opción de copiar al portapapeles.
                </p>

                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <p className="text-sm text-blue-800 dark:text-blue-300">
                    <strong>Premium Logs:</strong> Si necesitas retención extendida de logs (7 o 30 días), activa la opción
                    de Premium Logs durante la configuración del servicio. Esto permite analizar logs históricos más allá
                    de la sesión activa.
                  </p>
                </div>
              </section>

              {/* ═══════════════ TERMINAL ═══════════════ */}
              <section id="terminal" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Terminal</h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Las apps con backend en estado <strong>running</strong> tienen acceso a una terminal web segura.
                  Esto te permite ejecutar comandos directamente en la instancia donde se ejecuta tu aplicación.
                </p>

                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Acceso</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Desde el detalle de tu servicio, haz clic en el botón <strong>Conectar al Servidor</strong> en la tarjeta
                  de conexión del panel lateral. Se abrirá una terminal web con acceso seguro a tu instancia.
                </p>

                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Casos de Uso</h3>
                <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400 mb-4">
                  <li className="flex items-center gap-2"><CheckOutlined className="text-green-500 text-xs" /> Verificar el estado de tu aplicación con comandos del sistema</li>
                  <li className="flex items-center gap-2"><CheckOutlined className="text-green-500 text-xs" /> Ejecutar migraciones de base de datos</li>
                  <li className="flex items-center gap-2"><CheckOutlined className="text-green-500 text-xs" /> Inspeccionar archivos de configuración</li>
                  <li className="flex items-center gap-2"><CheckOutlined className="text-green-500 text-xs" /> Depurar problemas en tiempo real</li>
                </ul>

                <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <p className="text-sm text-amber-800 dark:text-amber-300">
                    <strong>Seguridad:</strong> La terminal opera a través de una conexión segura autenticada.
                    Solo los usuarios autorizados del tenant pueden acceder. No se usa SSH tradicional;
                    la conexión se establece mediante el agente de Kubiy.
                  </p>
                </div>
              </section>

              {/* ═══════════════ ESTADOS DEL SERVICIO ═══════════════ */}
              <section id="statuses" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Estados del Servicio</h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Tu servicio y cada app pasan por distintos estados durante su ciclo de vida.
                  Aquí te explicamos qué significa cada uno y qué acciones puedes tomar.
                </p>

                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Estados del Servicio</h3>
                <div className="overflow-x-auto mb-8">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 dark:border-gray-700">
                        <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Estado</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Indicador</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Descripción</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      <tr>
                        <td className="py-3 px-4"><code className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-xs font-mono">initializing</code></td>
                        <td className="py-3 px-4"><span className="inline-block w-2.5 h-2.5 rounded-full bg-yellow-400 animate-pulse"></span></td>
                        <td className="py-3 px-4 text-gray-600 dark:text-gray-400">El servicio se está creando por primera vez. Se provisiona la infraestructura (red, cómputo, certificado SSL).</td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4"><code className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-xs font-mono">deploying</code></td>
                        <td className="py-3 px-4"><span className="inline-block w-2.5 h-2.5 rounded-full bg-blue-400 animate-pulse"></span></td>
                        <td className="py-3 px-4 text-gray-600 dark:text-gray-400">Se está desplegando código en el servicio. Puede ser el deploy inicial o uno posterior.</td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4"><code className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-xs font-mono">running</code></td>
                        <td className="py-3 px-4"><span className="inline-block w-2.5 h-2.5 rounded-full bg-green-500"></span></td>
                        <td className="py-3 px-4 text-gray-600 dark:text-gray-400">El servicio está activo y funcionando correctamente. Tu app es accesible desde su URL pública.</td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4"><code className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-xs font-mono">stopped</code></td>
                        <td className="py-3 px-4"><span className="inline-block w-2.5 h-2.5 rounded-full bg-gray-400"></span></td>
                        <td className="py-3 px-4 text-gray-600 dark:text-gray-400">El servicio fue detenido manualmente. La infraestructura se mantiene pero la app no está sirviendo tráfico.</td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4"><code className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-xs font-mono">error</code></td>
                        <td className="py-3 px-4"><span className="inline-block w-2.5 h-2.5 rounded-full bg-red-500"></span></td>
                        <td className="py-3 px-4 text-gray-600 dark:text-gray-400">Ocurrió un error durante la creación o ejecución del servicio. Revisa los eventos para más detalles.</td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4"><code className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-xs font-mono">deleting</code></td>
                        <td className="py-3 px-4"><span className="inline-block w-2.5 h-2.5 rounded-full bg-red-400 animate-pulse"></span></td>
                        <td className="py-3 px-4 text-gray-600 dark:text-gray-400">El servicio está en proceso de eliminación. Se están destruyendo los recursos asociados.</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Estados de Deploy</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Cada despliegue individual también tiene su propio estado dentro del historial de deploys:
                </p>
                <div className="grid sm:grid-cols-2 gap-3 mb-6">
                  <div className="flex items-center gap-3 p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <span className="px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-300 rounded text-xs font-medium">PENDING</span>
                    <p className="text-xs text-gray-600 dark:text-gray-400">El deploy está en cola, esperando ser procesado.</p>
                  </div>
                  <div className="flex items-center gap-3 p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded text-xs font-medium">IN_PROGRESS</span>
                    <p className="text-xs text-gray-600 dark:text-gray-400">El deploy se está ejecutando (clonando, instalando, compilando).</p>
                  </div>
                  <div className="flex items-center gap-3 p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300 rounded text-xs font-medium">SUCCESS</span>
                    <p className="text-xs text-gray-600 dark:text-gray-400">El deploy finalizó correctamente y la app está actualizada.</p>
                  </div>
                  <div className="flex items-center gap-3 p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <span className="px-2 py-0.5 bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300 rounded text-xs font-medium">FAILED</span>
                    <p className="text-xs text-gray-600 dark:text-gray-400">El deploy falló. Revisa los logs de deploy para diagnosticar.</p>
                  </div>
                </div>

                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Acciones Disponibles por Estado</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Las acciones que puedes realizar dependen del estado actual del servicio:
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 dark:border-gray-700">
                        <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Acción</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Disponible en</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Descripción</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      <tr>
                        <td className="py-3 px-4 font-medium text-gray-900 dark:text-white">▶ Iniciar</td>
                        <td className="py-3 px-4"><code className="bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded text-xs font-mono">stopped</code></td>
                        <td className="py-3 px-4 text-gray-600 dark:text-gray-400">Arranca el servicio detenido</td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 font-medium text-gray-900 dark:text-white">❚❚ Detener</td>
                        <td className="py-3 px-4"><code className="bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded text-xs font-mono">running</code></td>
                        <td className="py-3 px-4 text-gray-600 dark:text-gray-400">Detiene el servicio sin eliminar la infraestructura</td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 font-medium text-gray-900 dark:text-white">↻ Reiniciar</td>
                        <td className="py-3 px-4"><code className="bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded text-xs font-mono">running</code></td>
                        <td className="py-3 px-4 text-gray-600 dark:text-gray-400">Reinicia el proceso de la app (útil después de cambiar variables de entorno)</td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 font-medium text-gray-900 dark:text-white">🗑 Eliminar</td>
                        <td className="py-3 px-4 text-gray-600 dark:text-gray-400">Todos (excepto <code className="bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded text-xs font-mono">deleting</code>)</td>
                        <td className="py-3 px-4 text-gray-600 dark:text-gray-400">Elimina el servicio y todos sus recursos de forma permanente</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <p className="text-sm text-blue-800 dark:text-blue-300">
                    <strong>Tip:</strong> Durante los estados <code className="font-mono">initializing</code> y <code className="font-mono">deploying</code>,
                    el dashboard hace polling automático y actualiza la interfaz en tiempo real. No necesitas refrescar la página manualmente.
                  </p>
                </div>
              </section>

            </div>
          </main>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-gray-200 dark:border-gray-800 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <span className="text-sm text-gray-500 dark:text-gray-400">
            &copy; {new Date().getFullYear()} Kubiy Cloud. Todos los derechos reservados.
          </span>
          <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
            <Link to="/" className="hover:text-gray-900 dark:hover:text-white transition-colors">kubiy.cloud</Link>
            <span>&middot;</span>
            <a href="#" className="hover:text-gray-900 dark:hover:text-white transition-colors">Términos</a>
            <span>&middot;</span>
            <a href="#" className="hover:text-gray-900 dark:hover:text-white transition-colors">Privacidad</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DocsDeployApp;